import React, { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import MultiSelectFilter from "../MultiSelectFilter";
import {
  selectAllFilterOptions,
  selectAppliedFilters,
} from "../AdministeredUsers.state";
import { Box } from "@thecb/components";
import { toTitleCase } from "@/util/common";

const EDGE_MARGIN = 8;
const DROPDOWN_SELECTOR = "[id$='-filter-dropdown']";

const MultiSelectFilterBar = () => {
  const availableFilters = useSelector(selectAllFilterOptions);
  const appliedFilters = useSelector(selectAppliedFilters);
  const barRef = useRef(null);

  const getFilterInputOptions = (values) =>
    values.map((filterVal) => ({
      name: toTitleCase(filterVal),
      value: filterVal,
    }));

  const availableFiltersList = Object.keys(availableFilters).map((key) => ({
    name: key,
    options: getFilterInputOptions(availableFilters[key]),
  }));

  // While MultipleSelectFilter panel is open, pin its chip's footprint so the
  // row doesn't jump locations or get clipped past edge of screen
  const syncOpenDropdowns = useCallback(() => {
    if (!barRef.current) {
      return;
    }
    Array.from(barRef.current.children).forEach((chip) => {
      const dropdown = chip.querySelector(DROPDOWN_SELECTOR);

      if (!dropdown) {
        delete chip.dataset.filterOpen;
        chip.style.width = "";
        chip.style.flex = "";
        return;
      }

      if (!chip.dataset.filterOpen) {
        const { width } = chip.getBoundingClientRect();
        chip.dataset.filterOpen = "true";
        chip.style.width = `${width}px`;
        chip.style.flex = `0 0 ${width}px`;
      }

      dropdown.style.left = "";
      dropdown.style.right = "";
      const limit = document.documentElement.clientWidth - EDGE_MARGIN;
      if (dropdown.getBoundingClientRect().right <= limit) {
        return;
      }
      dropdown.style.left = "auto";
      dropdown.style.right = "0";
      // don't allow clipped left or right edge
      if (dropdown.getBoundingClientRect().left < EDGE_MARGIN) {
        dropdown.style.left = "";
        dropdown.style.right = "";
      }
    });
  }, []);

  useEffect(() => {
    if (!barRef.current) {
      return undefined;
    }
    const observer = new MutationObserver(syncOpenDropdowns);
    observer.observe(barRef.current, { childList: true, subtree: true });
    window.addEventListener("resize", syncOpenDropdowns);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncOpenDropdowns);
    };
  }, [syncOpenDropdowns, availableFiltersList.length]);

  return (
    <>
      {availableFiltersList?.length ? (
        <Box
          ref={barRef}
          padding="0.5rem 0 0"
          extraStyles={`
            display: flex;
            flex-wrap: wrap;
            flex-direction: row;
            gap: 0.5rem;

            /* Ellipsize the label rather than overflow a pinned chip */
            [data-filter-open] [data-qa$="-filter-button"] {
              max-width: 100%;
            }
            [data-filter-open] [data-qa$="-filter-button"] span {
              min-width: 0;
            }
            [data-filter-open] [data-qa$="-filter-button"] svg {
              flex-shrink: 0;
            }
          `}
        >
          {availableFiltersList.map((filter, index) => (
            <MultiSelectFilter
              key={index}
              filterName={filter.name}
              options={filter.options}
              appliedFilters={appliedFilters[filter.name]}
            />
          ))}
        </Box>
      ) : null}
    </>
  );
};

export default MultiSelectFilterBar;
